import React from 'react';
import { 
  Box, 
  TextField, 
  Typography, 
  Paper,
  Grid,
  InputAdornment,
  IconButton,
  Tooltip,
  useTheme
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import NoteIcon from '@mui/icons-material/Note';

interface CustomerInfoProps {
  formData: {
    clientName: string;
    notes?: string;
    date: string;
  };
  onFormDataChange: (formData: { clientName: string; notes?: string; date: string }) => void;
}

const CustomerInfo: React.FC<CustomerInfoProps> = ({ formData, onFormDataChange }) => {
  const theme = useTheme();

  const handleChange = (field: 'clientName' | 'notes' | 'date') => (event: React.ChangeEvent<HTMLInputElement>) => {
    onFormDataChange({
      ...formData,
      [field]: event.target.value
    });
  };

  // Format date for input field (YYYY-MM-DD)
  const formatDateForInput = (dateString: string) => {
    if (!dateString) {
      // Return today's date if no date is provided
      return new Date().toISOString().split('T')[0];
    }
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return new Date().toISOString().split('T')[0];
      }
      return date.toISOString().split('T')[0];
    } catch (error) {
      return new Date().toISOString().split('T')[0];
    }
  };

  return (
    <Paper 
      elevation={2} 
      sx={{ 
        p: 4, 
        mb: 4,
        borderRadius: 2,
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: theme.shadows[4]
        }
      }}
    >
      <Typography 
        variant="h5" 
        gutterBottom 
        sx={{ 
          color: 'primary.main',
          fontWeight: 600,
          mb: 4,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}
      >
        Informations du client
      </Typography>
      
      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Nom du Client"
            value={formData.clientName}
            onChange={handleChange('clientName')}
            required
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon color="primary" />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: 'primary.main',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main',
                  borderWidth: 2
                }
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: 'primary.main'
              }
            }}
          />
        </Grid>
        
        <Grid item xs={12} md={6}>
          <TextField
            label="Date prévue"
            type="date"
            value={formatDateForInput(formData.date)}
            onChange={handleChange('date')}
            required
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CalendarTodayIcon color="primary" />
                </InputAdornment>
              ),
            }}
            InputLabelProps={{
              shrink: true,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: 'primary.main',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main',
                  borderWidth: 2
                }
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: 'primary.main'
              }
            }}
          />
        </Grid>
        
        <Grid item xs={12}>
          <TextField
            label="Notes"
            value={formData.notes || ''}
            onChange={handleChange('notes')}
            multiline
            rows={3}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <NoteIcon color="primary" />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: 'primary.main',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main',
                  borderWidth: 2
                }
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: 'primary.main'
              }
            }}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

export default CustomerInfo; 